import { useState, useEffect, useCallback, useRef } from 'react';
import logger from '@/utils/logger';

/**
 * Hook personnalisé pour gérer le rafraîchissement automatique des routes
 * Gère la visibilité de la page, le focus de la fenêtre et les changements de données
 */
const useRouteRefresh = (reloadFunction, dependencies = []) => {
  const [isVisible, setIsVisible] = useState(!document.hidden);
  const [lastRefresh, setLastRefresh] = useState(Date.now());
  const reloadTimeoutRef = useRef(null);
  const lastDependenciesRef = useRef(JSON.stringify(dependencies));

  // Fonction pour déclencher un rechargement avec debouncing
  const triggerReload = useCallback(
    (reason, delay = 200) => {
      logger.debug(`🔄 Déclenchement rechargement: ${reason}`);

      // Annuler tout rechargement en attente
      if (reloadTimeoutRef.current) {
        clearTimeout(reloadTimeoutRef.current);
      }

      // Programmer le rechargement
      reloadTimeoutRef.current = setTimeout(() => {
        if (typeof reloadFunction === 'function') {
          reloadFunction();
          setLastRefresh(Date.now());
        }
        reloadTimeoutRef.current = null;
      }, delay);
    },
    [reloadFunction],
  );

  // Détection des changements de visibilité
  useEffect(() => {
    const handleVisibilityChange = () => {
      const visible = !document.hidden;
      const wasVisible = isVisible;
      setIsVisible(visible);

      if (visible && !wasVisible) {
        triggerReload('page visible', 200);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isVisible, triggerReload]);

  // Détection du focus/blur de la fenêtre
  useEffect(() => {
    const handleWindowFocus = () => {
      if (isVisible) {
        triggerReload('window focus', 100);
      }
    };

    const handleWindowBlur = () => {
      // Annuler tout rechargement en attente
      if (reloadTimeoutRef.current) {
        clearTimeout(reloadTimeoutRef.current);
        reloadTimeoutRef.current = null;
      }
    };

    window.addEventListener('focus', handleWindowFocus);
    window.addEventListener('blur', handleWindowBlur);

    return () => {
      window.removeEventListener('focus', handleWindowFocus);
      window.removeEventListener('blur', handleWindowBlur);
    };
  }, [isVisible, triggerReload]);

  // Détection des changements de dépendances
  useEffect(() => {
    const currentDependencies = JSON.stringify(dependencies);

    if (currentDependencies !== lastDependenciesRef.current) {
      logger.debug('📊 Changement détecté dans les dépendances');
      lastDependenciesRef.current = currentDependencies;

      // Délai plus court pour les changements de données
      triggerReload('dependencies changed', 50);
    }
  }, [dependencies, triggerReload]);

  // Nettoyage au démontage
  useEffect(() => {
    return () => {
      if (reloadTimeoutRef.current) {
        clearTimeout(reloadTimeoutRef.current);
      }
    };
  }, []);

  // Fonction pour forcer un rechargement immédiat
  const forceReload = useCallback(
    (reason = 'manual') => {
      if (reloadTimeoutRef.current) {
        clearTimeout(reloadTimeoutRef.current);
      }

      logger.debug(`🚀 Rechargement forcé: ${reason}`);
      if (typeof reloadFunction === 'function') {
        reloadFunction();
        setLastRefresh(Date.now());
      }
    },
    [reloadFunction],
  );

  return {
    isVisible,
    lastRefresh,
    triggerReload,
    forceReload,
  };
};

export default useRouteRefresh;
